import { useQuery, useQueryClient } from '@tanstack/react-query';
import { suppliersAPI } from '../../services/api';
import { SupplierListResponse } from '../../types/supplier';

/**
 * Query hooks for Suppliers management (server-paginated list + deleted list).
 * Key root 'suppliers' matches the realtime-invalidation slug, so a change by
 * any user (and the shared useSuppliersDropdown) auto-refreshes together.
 */
export interface SuppliersListParams {
  skip: number;
  limit: number;
  search?: string;
  extra?: Record<string, any>;
}

export const suppliersKeys = {
  root: ['suppliers'] as const,
  list: (p: SuppliersListParams) => ['suppliers', 'list', p] as const,
  deleted: ['suppliers', 'deleted'] as const,
};

export function useSuppliersList(params: SuppliersListParams, enabled = true) {
  return useQuery<SupplierListResponse>({
    queryKey: suppliersKeys.list(params),
    queryFn: () => suppliersAPI.getAll(params.skip, params.limit, params.search, undefined, params.extra),
    enabled,
    keepPreviousData: true,
  });
}

export function useDeletedSuppliers(enabled: boolean) {
  return useQuery<SupplierListResponse>({
    queryKey: suppliersKeys.deleted,
    queryFn: () => suppliersAPI.getDeleted(),
    enabled,
  });
}

export function useInvalidateSuppliers() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: suppliersKeys.root });
}
